import React from "react";
import { axiosWithoutAuth } from "../../../axios/axiosInstance";

/**
 * Print module for the Pay Account invoice.
 * Self-contained: renders the "Print" button and owns the print flow — fetches
 * the invoice HTML and prints it via a hidden iframe (reliable, no popup blocker).
 *
 * Props:
 *  - transactionId, companyId : request fields
 *  - totalAmount              : formatted total string for the request
 */
export const PrintInvoiceButton = ({ transactionId, companyId, totalAmount }) => {
  const printDiv = async () => {
    const reqdata = {
      TransactionID: String(transactionId),
      company_Id: companyId ? String(companyId) : "0",
      totalAmount: String(totalAmount ?? "0"),
    };

    try {
      const response = await axiosWithoutAuth.post("PayAccount/GetPayInvoiceHtml", reqdata);
      const html = response?.data;
      if (!html || html === "-1") {
        console.error("Failed to fetch invoice HTML for print");
        return;
      }

      // Use a hidden iframe so the print dialog shows reliably (no popup blocker)
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const cleanup = () => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      };

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();

      // Inject print styles: clean page margins + force background colors to print
      const printStyles = `
        @page { size: A4; margin: 12mm; }
        html, body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body { background-color: #ffffff !important; }
      `;
      const styleEl = doc.createElement("style");
      styleEl.setAttribute("media", "print");
      styleEl.appendChild(doc.createTextNode(printStyles));
      (doc.head || doc.documentElement).appendChild(styleEl);

      const triggerPrint = () => {
        const win = iframe.contentWindow;
        win.focus();
        win.onafterprint = cleanup;
        win.print();
        // Fallback cleanup in case onafterprint never fires (some browsers)
        setTimeout(cleanup, 60000);
      };

      if (iframe.contentWindow.document.readyState === "complete") {
        // give the browser a tick to lay out the content before printing
        setTimeout(triggerPrint, 250);
      } else {
        iframe.onload = () => setTimeout(triggerPrint, 250);
      }
    } catch (error) {
      console.error("Error fetching invoice HTML for print:", error);
    }
  };

  return (
    <div className="button-effect">
      <button
        className="btn btn-products theme-primary"
        style={{
          backgroundColor: "#0087eb",
          color: "white",
        }}
        onClick={printDiv}
      >
        Print
      </button>
    </div>
  );
};

export default PrintInvoiceButton;
